/**
 * DateTime 統一處理工具
 *
 * 用途：
 * - 自動將後端 UTC 時間轉換為瀏覽器本地時區
 * - 提供統一的時間格式化方法
 *
 * 核心原則：
 * - 後端永遠使用 UTC 時間（ISO 8601 格式）
 * - 前端自動轉換為使用者本地時區
 * - 瀏覽器自動偵測時區，無需手動設定
 */

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

// 載入 UTC 插件
dayjs.extend(utc);

type DateTimeInput = string | Date | null | undefined;

/**
 * 格式化 DateTime（含時分秒）
 *
 * @param dateTime - ISO 8601 格式的時間字串或 Date 物件
 * @param format - 輸出格式，預設 'YYYY-MM-DD HH:mm:ss'
 * @returns 格式化後的本地時間
 *
 * @example
 * formatDateTime("2020-11-30T07:11:28.000Z")
 * // 在台北 (GMT+8): "2020-11-30 15:11:28"
 * // 在紐約 (GMT-5): "2020-11-30 02:11:28"
 */
export function formatDateTime(dateTime: DateTimeInput, format: string = 'YYYY-MM-DD HH:mm:ss'): string {
  if (!dateTime) return '-';

  try {
    // dayjs 會自動將 UTC 時間轉換為瀏覽器本地時區
    const parsed = dayjs(dateTime);
    if (!parsed.isValid()) return '-';
    return parsed.format(format);
  } catch (error) {
    console.error('[formatDateTime] Error:', error);
    return '-';
  }
}

/**
 * 格式化 DateTime（短格式，不含秒）
 *
 * @param dateTime - ISO 8601 格式的時間字串
 * @returns 格式化後的本地時間（不含秒）
 *
 * @example
 * formatDateTimeShort("2020-11-30T07:11:28Z")
 * // 在台北: "2020-11-30 15:11"
 */
export function formatDateTimeShort(dateTime: DateTimeInput): string {
  return formatDateTime(dateTime, 'YYYY-MM-DD HH:mm');
}

/**
 * 格式化純日期
 *
 * @param date - 日期字串或 Date 物件
 * @returns 格式化後的日期
 *
 * @example
 * formatDate("2020-11-30")
 * // "2020-11-30"
 */
export function formatDate(date: DateTimeInput): string {
  if (!date) return '-';

  try {
    return dayjs(date).format('YYYY-MM-DD');
  } catch (error) {
    console.error('[formatDate] Error:', error);
    return '-';
  }
}

/**
 * 格式化相對時間（如：3 小時前）
 *
 * @param dateTime - ISO 8601 格式的時間字串
 * @returns 相對時間
 *
 * @example
 * formatRelativeTime("2020-11-30T07:11:28Z")
 * // "3 年前"
 */
export function formatRelativeTime(dateTime: DateTimeInput): string {
  if (!dateTime) return '-';

  try {
    const now = dayjs();
    const target = dayjs(dateTime);

    const diffMinutes = now.diff(target, 'minute');
    const diffHours = now.diff(target, 'hour');
    const diffDays = now.diff(target, 'day');
    const diffMonths = now.diff(target, 'month');
    const diffYears = now.diff(target, 'year');

    if (diffMinutes < 1) return '剛剛';
    if (diffMinutes < 60) return `${diffMinutes} 分鐘前`;
    if (diffHours < 24) return `${diffHours} 小時前`;
    if (diffDays < 30) return `${diffDays} 天前`;
    if (diffMonths < 12) return `${diffMonths} 個月前`;
    return `${diffYears} 年前`;
  } catch (error) {
    console.error('[formatRelativeTime] Error:', error);
    return '-';
  }
}

/**
 * 計算從今天到目標日期的整日數（calendar-day diff，含時區歸零）
 *
 * 兩邊都先 startOf('day') 才相減，避免「今天 11:34 到目標 00:00」這種小時殘留
 * 造成 floor 後少 1 天的問題。語意：「再過 N 天就會到目標日期」（exclusive，不含今天）。
 *
 * @param target - 目標日期（ISO 字串、Date、null/undefined）
 * @returns 整數天數；目標為 null/invalid 或已過去回傳 0
 *
 * @example
 * // 今天 2026-05-06，目標 2026-05-20
 * daysUntilDate('2026-05-20T00:00:00+08:00') // 14
 */
export function daysUntilDate(target: DateTimeInput): number {
  if (!target) return 0;

  try {
    const parsed = dayjs(target);
    if (!parsed.isValid()) return 0;

    const diff = parsed.startOf('day').diff(dayjs().startOf('day'), 'day');
    return Math.max(diff, 0);
  } catch (error) {
    console.error('[daysUntilDate] Error:', error);
    return 0;
  }
}

/**
 * 把 API 回來的字串日期轉成 Dayjs（給 antd DatePicker 用）；invalid / 空值 → null。
 *
 * 寫進 antd `<Form.Item>` 的 date 欄位**必須**是 Dayjs，不能是字串——一旦 DatePicker
 * mount 起來會跑 `date.isValid()`，字串沒這個方法就 crash。用這個 helper 把
 * 「BE 字串」與「FE form 物件」邊界一次處理掉。
 */
export function toDayjsOrNull(
  value: string | Date | dayjs.Dayjs | null | undefined,
): dayjs.Dayjs | null {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
}

/**
 * 取得使用者當前時區偏移量
 *
 * @returns 時區顯示名稱
 *
 * @example
 * getTimezoneDisplay()
 * // "GMT+8"
 */
export function getTimezoneDisplay(): string {
  const offset = dayjs().utcOffset() / 60;
  const sign = offset >= 0 ? '+' : '';
  return `GMT${sign}${offset}`;
}

/**
 * 取得使用者當前時區名稱
 *
 * @returns 時區名稱
 *
 * @example
 * getUserTimezone()
 * // "Asia/Taipei"
 */
export function getUserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.error('[getUserTimezone] Error:', error);
    return 'UTC';
  }
}

// 預設匯出
export default {
  formatDateTime,
  formatDateTimeShort,
  formatDate,
  formatRelativeTime,
  daysUntilDate,
  toDayjsOrNull,
  getTimezoneDisplay,
  getUserTimezone,
};
